// src/components/core/MigrationModal/tabs/EnrichmentTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Switch, Tooltip, Segmented, Alert, theme } from 'antd';
import { QuestionCircleOutlined, ApiOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { onProduction } from 'utils/helpers';
import aiGenerativeIcon from 'assets/images/ai-generative.png';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EnrichmentTab = ({
  hasToken,
  locked,
  conflictStrategy,
  enrichRecipientByCnpj,
  enrichRecipientByCpf,
  enrichProducts,
  enrichmentPrices,
  cpfEnabled = false,
  // Visibility props (default true for backwards compatibility)
  showCnpjEnrichment = true,
  showCpfEnrichment = true,
  showProductsEnrichment = true,
  onConflictStrategyChange,
  onEnrichRecipientByCnpjChange,
  onEnrichRecipientByCpfChange,
  onEnrichProductsChange,
}) => {
  const { token: antToken } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = locked || !hasToken;
  const isDarkMode = antToken.colorBgBase === '#000' || antToken.colorBgContainer === '#141414';

  // Enrichment costs (in credits) - 1 credit = R$ 0.10
  const cnpjCredits = Math.ceil((enrichmentPrices?.recipientByCnpj || 0) * 10);
  const cpfCredits = Math.ceil((enrichmentPrices?.recipientByCpf || 0) * 10);
  const productsCredits = Math.ceil((enrichmentPrices?.products || 0) * 10);

  // ─────────────────────────────────────────────────────────────────────────────
  // STYLES
  // ─────────────────────────────────────────────────────────────────────────────
  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
  };

  const lastRowStyle = {
    ...rowStyle,
    borderBottom: 'none',
  };

  const titleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ opacity: hasToken ? 1 : 0.5 }}>
      {/* Row 1: Conflict Strategy */}
      <div style={rowStyle}>
        <div style={titleContainerStyle}>
          <Text>{translateX('data_conflict')}</Text>
          <Tooltip title={translateX('data_conflict_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'normal', fontSize: 12 }} />
          </Tooltip>
        </div>
        <Segmented
          size="small"
          value={conflictStrategy}
          onChange={onConflictStrategyChange}
          disabled={isDisabled}
          options={[
            { label: translateX('ignore'), value: 'ignore' },
            { label: translateX('overwrite'), value: 'overwrite' },
          ]}
        />
      </div>

      {/* Row 2: CNPJ Enrichment */}
      {showCnpjEnrichment && (
        <div style={rowStyle}>
          <div style={titleContainerStyle}>
            <Text>{translateX('cnpj')}</Text>
            <Tooltip title={translateX('enrichment_by_api')}>
              <ApiOutlined style={{ color: antToken.colorText, fontSize: 14 }} />
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 12 }}>
              (~{cnpjCredits} {translateX('credits')})
            </Text>
            <Tooltip title={translateX('enrich_cnpj_charge_tooltip')}>
              <QuestionCircleOutlined
                style={{ color: '#8c8c8c', cursor: 'normal', fontSize: 12 }}
              />
            </Tooltip>
          </div>
          <Switch
            size="small"
            checked={enrichRecipientByCnpj}
            onChange={onEnrichRecipientByCnpjChange}
            disabled={isDisabled}
          />
        </div>
      )}

      {/* Row 3: CPF Enrichment */}
      {showCpfEnrichment && (
        <div style={{ ...rowStyle, opacity: cpfEnabled ? 1 : 0.5 }}>
          <div style={titleContainerStyle}>
            <Text>{translateX('cpf')}</Text>
            <Tooltip title={translateX('enrichment_by_api')}>
              <ApiOutlined style={{ color: antToken.colorText, fontSize: 14 }} />
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 12 }}>
              (~{cpfCredits} {translateX('credits')})
            </Text>
            <Tooltip
              title={
                cpfEnabled
                  ? translateX('enrich_cpf_charge_tooltip')
                  : translateX('cpf_not_available')
              }
            >
              <QuestionCircleOutlined
                style={{ color: '#8c8c8c', cursor: 'normal', fontSize: 12 }}
              />
            </Tooltip>
          </div>
          <Switch
            size="small"
            checked={enrichRecipientByCpf}
            onChange={onEnrichRecipientByCpfChange}
            disabled={isDisabled || !cpfEnabled}
          />
        </div>
      )}

      {/* Row 4: Products Enrichment (AI) */}
      {showProductsEnrichment && (
        <div style={lastRowStyle}>
          <div style={titleContainerStyle}>
            <Text>{translateX('products')}</Text>
            <Tooltip title={translateX('enrichment_by_ai')}>
              <img
                src={aiGenerativeIcon}
                alt="AI"
                style={{
                  width: 14,
                  height: 14,
                  filter: isDarkMode ? 'invert(1)' : 'none',
                  // cursor: 'normal',
                }}
              />
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 12 }}>
              (~{productsCredits} {translateX('credits')})
            </Text>
            <Tooltip title={translateX('enrich_products_charge_tooltip')}>
              <QuestionCircleOutlined
                style={{ color: '#8c8c8c', cursor: 'normal', fontSize: 12 }}
              />
            </Tooltip>
          </div>
          <Switch
            size="small"
            checked={enrichProducts}
            onChange={onEnrichProductsChange}
            disabled={isDisabled || onProduction()}
          />
        </div>
      )}

      <Alert type="info" className="mt-3" showIcon title={translateX('enrichment_info')} />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EnrichmentTab;
